<template>
  <ComboboxRoot
    :modelValue="modelValue"
    :multiple="multiple"
    :open="open"
    :filter-function="handleSearching"
    v-model:searchTerm="searchTerm"
    ref="filterRef"
    class="relative"
    @click.stop
  >
    <ComboboxAnchor>
      <Trigger :label="triggerLabel" @click="handleOpen()">
        <template #trigger>
          <Selection
            v-if="getSelectionText?.length && showSelection"
            :name="name"
            :text="getSelectionText"
            @remove="handleRemoveFilter"
          />
        </template>
      </Trigger>
    </ComboboxAnchor>
    <ComboboxContent
      :class="!isSelectingCustomDate ? 'w-[240px] max-h-[400px]' : 'w-[328px]'"
      class="absolute z-10 mt-2 bg-primary overflow-hidden rounded-md border border-secondary shadow-lg"
    >
      <Search
        v-if="showSearch"
        :placeholder="inputPlaceholder"
        :cancelLabel="cancelLabel"
        :showCancel="!isEmpty(searchTerm)"
      />
      <ComboboxViewport class="pb-2" :class="{ 'pt-2': !showSearch }">
        <template v-if="isSelectingCustomDate">
          <DateRangePicker @rangeSelected="handleCustomRangeSelected" />
        </template>
        <template v-if="!isSelectingCustomDate">
          <ComboboxEmpty
            class="text-style-sm font-medium text-secondary px-3 py-2"
          />
          <Group
            v-for="(group, index) in groups"
            :options="group.options"
            :key="index"
            :type="type"
            :handle="handle"
            :modelValue="modelValue"
            :multiple="multiple"
            :divider="last(groups) === group && groups.length > 1"
            @select="(event) => handleUpdateModelValue(event)"
          />
        </template>
      </ComboboxViewport>
      <div v-if="showClear">
        <ComboboxSeparator class="border-secondary border-t" />
        <div class="flex justify-between items-center px-3 py-2">
          <Clear
            v-if="showClear"
            @clear="handleClear"
            :label="clearLabel"
            :disabled="!hasSelectedItems"
          />
        </div>
      </div>
    </ComboboxContent>
  </ComboboxRoot>
</template>

<script setup>
// Libraries
import { ref, computed, watchEffect } from "vue";
import { last, isString, isEmpty, clone } from "lodash";

// Components
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxSeparator,
  ComboboxEmpty,
  ComboboxRoot,
  ComboboxViewport,
} from "radix-vue";
import { Search, Clear, Trigger, Selection } from "@/SharedComponents/Combobox";
import { Group } from "@/SharedComponents/Filters/Combobox";
import { DateRangePicker } from "@/SharedComponents/Date";

// Composables
import { useClickOutside } from "@/Composables/useClickOutside";

// Utils
import { useArrayToggle } from "@/Utils/arrayToggle";
import { formatLabels } from "@/Utils/formatLabels";
import { formatDateRange } from "@/Utils/formatDateRange";

// Emits
const emit = defineEmits(["update:open", "removeFilter"]);

// Props
const props = defineProps({
  type: {
    type: String,
    default: "default",
  },
  handle: {
    type: String,
    required: false,
  },
  name: {
    type: String,
    required: false,
  },
  showClear: {
    type: Boolean,
    default: false,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  showSelection: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: [Array, Object, String],
    default: () => [],
  },
  triggerLabel: {
    type: String,
    required: false,
  },
  inputPlaceholder: {
    type: String,
    required: false,
  },
  cancelLabel: {
    type: String,
    required: false,
  },
  clearLabel: {
    type: String,
    required: false,
  },
  groups: {
    type: Array,
    required: true,
  },
  open: {
    type: Boolean,
    default: false,
  },
});

// Refs
const isSelectingCustomDate = ref(false);
const filterRef = ref(null);
const modelValue = ref(clone(props.modelValue));
const searchTerm = ref("");

// Imported functions
const { toggleArrayItem } = useArrayToggle();

// Computed props
const hasSelectedItems = computed(() => {
  return !isEmpty(modelValue.value);
});

const multiple = computed(() => {
  return (
    props.type === "checkbox" || props.type === "avatar" || props.type === "dot"
  );
});

// Watchers
watchEffect(() => {
  if (!props.open && !hasSelectedItems.value) {
    emit("removeFilter");
  }
});

// Functions
const handleOpen = () => {
  emit("update:open", !props.open);
};

const getSelectionText = computed(() => {
  if (modelValue.value.range) {
    return formatDateRange(modelValue.value);
  }

  const selected = props.groups.flatMap((group) =>
    group.options.filter((option) => {
      if (props.handle === "categories" && option.ids) {
        return (
          option.ids.some((id) => modelValue.value.includes(id)) ||
          modelValue.value.includes(option.id)
        );
      }
      return isString(modelValue.value)
        ? modelValue.value === option.id
        : modelValue.value.includes(option.id);
    })
  );

  return formatLabels(selected);
});

const handleSearching = (items) => {
  if (searchTerm.value === "") {
    return items;
  }

  return items.filter(
    (item) =>
      item.label?.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      item.fullname?.toLowerCase().includes(searchTerm.value.toLowerCase())
  );
};

const handleRemoveFilter = () => {
  handleClear();
  emit("removeFilter");
};

const handleClear = () => {
  modelValue.value = [];
  emit("update:modelValue", modelValue.value);
};

const handleUpdateModelValue = (event) => {
  const option = event.detail.value;

  switch (props.type) {
    case "avatar":
      handleMultiSelector(option);
      break;
    case "checkbox":
      handleMultiSelector(option);
      break;
    case "dot":
      handleMultiSelector(option);
      break;
    case "date":
      handleDateSelector(option);
      break;
    case "single":
      handleSingleSelector(option);
      break;
    default:
      handleMainSelector(option);
      break;
  }

  emit("update:modelValue", modelValue.value);
};

const handleMultiSelector = (option) => {
  const ids =
    props.handle === "categories" && option.ids ? option.ids : [option.id];
  ids.forEach((id) => toggleArrayItem(modelValue.value, id));
};

const handleDateSelector = (option) => {
  if (option.id === "custom") {
    isSelectingCustomDate.value = true;
  } else {
    modelValue.value = {
      shortcutId: option.id,
      label: option.label,
      range: option.range(),
    };
    emit("update:open", false);
  }
};

const handleSingleSelector = (option) => {
  modelValue.value = modelValue.value === option.id ? "" : [option.id];
  emit("update:open", false);
};

const handleMainSelector = (option) => {
  modelValue.value = [option];
  emit("update:open", false);
};

const handleCustomRangeSelected = (value) => {
  modelValue.value = {
    shortcutId: "custom",
    range: value,
  };

  emit("update:modelValue", modelValue.value);
  isSelectingCustomDate.value = false;
  emit("update:open", false);
};

useClickOutside(filterRef, () => {
  isSelectingCustomDate.value = false;
  emit("update:open", false);
});
</script>
